import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Edit, LocalPrintshop, Logout } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { dischargeBaby } from "../../../api/baby";

const getStatusColor = (status) => {
  switch (status) {
    case "admitted":
      return "primary";
    case "under_observation":
      return "warning";
    case "discharged":
      return "success";
    case "referred":
      return "secondary";
    case "deceased":
      return "error";
    default:
      return "default";
  }
};

const DetailCard = ({ title, rows }) => (
  <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, height: "100%" }}>
    <Typography variant="h6" fontWeight={700} mb={2}>
      {title}
    </Typography>
    <Stack spacing={1.5}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Typography variant="caption" color="text.secondary">
            {row.label}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {row.value || "N/A"}
          </Typography>
        </Box>
      ))}
    </Stack>
  </Paper>
);

export default function BabyInformation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user?.user;
  const { t } = useTranslation();

  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [dischargeData, setDischargeData] = useState({
    discharge_date: "",
    discharge_time: "",
    discharge_condition: "",
    discharge_notes: "",
    discharged_by: user?.id || "",
  });
  const [submittingDischarge, setSubmittingDischarge] = useState(false);

  const { data: baby, isLoading, refetch } = useFetchObjects(
    ["baby", id],
    `babies/${id}/`,
    token
  );

  const printHtml = useMemo(() => {
    if (!baby) return "";

    const title = t("baby.info.printTitle", "Baby Information");
    const babyIdLabel = t("baby.info.babyId", "Baby ID");
    const babyNameLabel = t("baby.list.babyName", "Baby Name");
    const statusLabel = t("baby.list.status", "Status");
    const babyDetailsTitle = t("baby.info.babyDetails", "Baby Details");
    const motherDetailsTitle = t("baby.info.motherDetails", "Mother Details");
    const registeredByLabel = t("baby.info.registeredBy", "Registered By");
    const registrationDateLabel = t("baby.info.registrationDate", "Registration Date");
    const lastUpdatedByLabel = t("baby.info.lastUpdatedBy", "Last Updated By");
    const lastUpdatedDateLabel = t("baby.info.lastUpdatedDate", "Last Updated Date");

    return `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: "Segoe UI", sans-serif; margin: 0; padding: 24px; background: #eef2f7; color: #16324f; }
            .sheet { max-width: 900px; margin: 0 auto; background: #fff; border-radius: 18px; overflow: hidden; box-shadow: 0 12px 30px rgba(0,0,0,0.08); }
            .header { padding: 28px; background: linear-gradient(135deg, #0f4c81, #1f6aa5); color: #fff; }
            .title { margin: 0; font-size: 30px; }
            .subtitle { margin: 10px 0 0; opacity: 0.9; }
            .body { padding: 28px; }
            .hero { display: flex; justify-content: space-between; gap: 16px; padding: 20px; border: 1px solid #d8e7f5; background: #f8fbff; border-radius: 14px; margin-bottom: 24px; }
            .hero strong { display: block; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: #58708a; }
            .hero span { display: block; margin-top: 6px; font-size: 30px; font-weight: 800; color: #0f4c81; }
            .badge { display: inline-block; padding: 8px 14px; border-radius: 999px; background: #e8f3ec; color: #1f6b3b; font-weight: 700; }
            .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
            .card { border: 1px solid #e5ebf2; border-radius: 14px; padding: 18px; }
            .card h3 { margin: 0 0 14px; }
            .row { margin-bottom: 10px; }
            .row small { display: block; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7a8c; margin-bottom: 4px; }
            .row div { font-weight: 600; }
            @media print { body { background: #fff; padding: 0; } .sheet { box-shadow: none; border-radius: 0; } }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="header">
              <h1 class="title">${title}</h1>
              <p class="subtitle">${babyIdLabel}: ${baby.baby_code}</p>
            </div>
            <div class="body">
              <div class="hero">
                <div><strong>${babyNameLabel}</strong><span>${baby.baby_name || "N/A"}</span></div>
                <div><strong>${statusLabel}</strong><div class="badge">${baby.current_status_display || "N/A"}</div></div>
              </div>
              <div class="grid">
                <div class="card">
                  <h3>${babyDetailsTitle}</h3>
                  <div class="row"><small>${t("baby.form.dateOfBirth", "Date of Birth")}</small><div>${baby.date_of_birth || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.timeOfBirth", "Time of Birth")}</small><div>${baby.time_of_birth || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.list.gender", "Gender")}</small><div>${baby.gender_display || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.birthWeight", "Birth Weight")}</small><div>${baby.birth_weight || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.bloodGroup", "Blood Group")}</small><div>${baby.blood_group_display || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.deliveryType", "Delivery Type")}</small><div>${baby.delivery_type_display || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.birthType", "Birth Type")}</small><div>${baby.birth_type_display || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.babyCondition", "Baby Condition")}</small><div>${baby.baby_condition_display || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.admissionDate", "Admission Date")}</small><div>${baby.admission_date || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.notes", "Notes")}</small><div>${baby.notes || "N/A"}</div></div>
                </div>
                <div class="card">
                  <h3>${motherDetailsTitle}</h3>
                  <div class="row"><small>${t("baby.form.motherPatientId", "Mother Patient ID")}</small><div>${baby.mother_patient_id || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.motherName", "Mother Name")}</small><div>${baby.mother_name || "N/A"}</div></div>
                  <div class="row"><small>${t("baby.form.motherPhoneNumber", "Mother Phone Number")}</small><div>${baby.mother_phone_number || "N/A"}</div></div>
                  <div class="row"><small>${registeredByLabel}</small><div>${baby.registered_by_name || "N/A"}</div></div>
                  <div class="row"><small>${registrationDateLabel}</small><div>${baby.created_at || "N/A"}</div></div>
                  <div class="row"><small>${lastUpdatedByLabel}</small><div>${baby.updated_by_name || "N/A"}</div></div>
                  <div class="row"><small>${lastUpdatedDateLabel}</small><div>${baby.updated_at || "N/A"}</div></div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  }, [baby, t]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  useEffect(() => {
    if (baby && searchParams.get("print") === "1") {
      handlePrint();
    }
  }, [baby, searchParams, printHtml]);

  const handleDischarge = async () => {
    setSubmittingDischarge(true);
    try {
      await dischargeBaby(id, dischargeData, token);
      queryClient.invalidateQueries({ queryKey: ["babies"] });
      await refetch();
      toast.success(t("baby.list.dischargeSuccess", "Baby discharged successfully."));
      setDischargeOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("baby.list.dischargeError", "Unable to discharge baby."));
    } finally {
      setSubmittingDischarge(false);
    }
  };

  if (isLoading || !baby) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {baby.baby_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t("baby.info.babyId", "Baby ID")}: {baby.baby_code}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate("/baby/list")}>
              {t("baby.info.backToList", "Back to Baby List")}
            </Button>
            <Button variant="outlined" startIcon={<Edit />} onClick={() => navigate(`/baby/${baby.id}/edit`)}>
              {t("baby.info.edit", "Edit Baby")}
            </Button>
            <Button variant="outlined" startIcon={<LocalPrintshop />} onClick={handlePrint}>
              {t("baby.info.print", "Print Baby Information")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<Logout />}
              onClick={() => setDischargeOpen(true)}
              disabled={baby.current_status === "discharged"}
            >
              {t("baby.info.discharge", "Discharge Baby")}
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: "#f8fbff", border: "1px solid #d8e7f5", mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t("baby.info.babyId", "Baby ID")}
              </Typography>
              <Typography variant="h5" fontWeight={800} color="primary.main">
                {baby.baby_code}
              </Typography>
            </Box>
            <Chip label={baby.current_status_display} color={getStatusColor(baby.current_status)} />
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <DetailCard
              title={t("baby.info.babyDetails", "Baby Details")}
              rows={[
                { label: t("baby.list.babyId", "Baby ID"), value: baby.baby_code },
                { label: t("baby.list.babyName", "Baby Name"), value: baby.baby_name },
                { label: t("baby.list.dateOfBirth", "Date of Birth"), value: baby.date_of_birth },
                { label: t("baby.form.timeOfBirth", "Time of Birth"), value: baby.time_of_birth },
                { label: t("baby.list.gender", "Gender"), value: baby.gender_display },
                { label: t("baby.form.birthWeight", "Birth Weight"), value: baby.birth_weight },
                { label: t("baby.form.bloodGroup", "Blood Group"), value: baby.blood_group_display },
                { label: t("baby.form.deliveryType", "Delivery Type"), value: baby.delivery_type_display },
                { label: t("baby.form.birthType", "Birth Type"), value: baby.birth_type_display },
                { label: t("baby.form.babyCondition", "Baby Condition"), value: baby.baby_condition_display },
                { label: t("baby.form.admissionDate", "Admission Date"), value: baby.admission_date },
                { label: t("baby.form.currentStatus", "Current Status"), value: baby.current_status_display },
                { label: t("baby.form.notes", "Notes"), value: baby.notes },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DetailCard
              title={t("baby.info.motherDetails", "Mother Details")}
              rows={[
                { label: t("baby.form.motherPatientId", "Mother Patient ID"), value: baby.mother_patient_id },
                { label: t("baby.form.motherName", "Mother Name"), value: baby.mother_name },
                { label: t("baby.form.motherPhoneNumber", "Mother Phone Number"), value: baby.mother_phone_number },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DetailCard
              title={t("baby.info.staffDetails", "Staff Details")}
              rows={[
                { label: t("baby.info.registeredBy", "Registered By"), value: baby.registered_by_name },
                { label: t("baby.info.registrationDate", "Registration Date"), value: baby.created_at },
                { label: t("baby.info.lastUpdatedBy", "Last Updated By"), value: baby.updated_by_name },
                { label: t("baby.info.lastUpdatedDate", "Last Updated Date"), value: baby.updated_at },
              ]}
            />
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={dischargeOpen} onClose={() => setDischargeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("baby.info.dischargeDialog.title", "Discharge Baby")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              type="date"
              label={t("baby.info.dischargeDialog.date", "Discharge Date")}
              InputLabelProps={{ shrink: true }}
              value={dischargeData.discharge_date}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_date: e.target.value }))}
            />
            <TextField
              type="time"
              label={t("baby.info.dischargeDialog.time", "Discharge Time")}
              InputLabelProps={{ shrink: true }}
              value={dischargeData.discharge_time}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_time: e.target.value }))}
            />
            <TextField
              label={t("baby.info.dischargeDialog.condition", "Condition at Discharge")}
              value={dischargeData.discharge_condition}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_condition: e.target.value }))}
            />
            <TextField
              multiline
              minRows={3}
              label={t("baby.info.dischargeDialog.notes", "Discharge Notes")}
              value={dischargeData.discharge_notes}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_notes: e.target.value }))}
            />
            <TextField
              label={t("baby.info.dischargeDialog.dischargedBy", "Discharged By")}
              value={`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || ""}
              disabled
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDischargeOpen(false)}>{t("baby.form.cancel", "Cancel")}</Button>
          <Button variant="contained" color="warning" onClick={handleDischarge} disabled={submittingDischarge}>
            {submittingDischarge ? t("baby.form.save", "Saving...") : t("baby.info.dischargeDialog.confirm", "Confirm Discharge")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
